import { Dialog, DialogTitle, DialogBackdrop, Transition } from '@headlessui/react';
import React, { Fragment, useRef } from 'react'
import { useTranslation } from 'react-i18next';
import { resources, translations } from "../../translations/translationsConstants";

export default function PdfPreviewModal({ open, setOpen, blobUrl, title = "PDF Preview" }) {
    const cancelButtonRef = useRef(null);
    const { t: te } = useTranslation([resources.edit]);

    const handleDownload = () => {
        if (blobUrl) {
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = 'document.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handlePrint = () => {
        if (blobUrl) {
            const printWindow = window.open(blobUrl, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            }
        }
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="tailwind-preflight"
                initialFocus={cancelButtonRef}
                open={open}
                onClose={setOpen}
            >
                <div className="tw-fixed tw-z-10 tw-inset-0 tw-overflow-y-auto">
                    <div className="tw-flex tw-items-center tw-justify-center tw-min-h-screen tw-pt-4 tw-px-4 tw-pb-20 tw-text-center sm:tw-block sm:tw-p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="tw-ease-out tw-duration-300"
                            enterFrom="tw-opacity-0"
                            enterTo="tw-opacity-100"
                            leave="tw-ease-in tw-duration-200"
                            leaveFrom="tw-opacity-100"
                            leaveTo="tw-opacity-0"
                        >
                            <DialogBackdrop className="tw-fixed tw-inset-0 tw-bg-gray-500 tw-bg-opacity-75 tw-transition-opacity" />
                        </Transition.Child>

                        <span className="tw-hidden sm:tw-inline-block sm:tw-align-middle sm:tw-h-screen" aria-hidden="true">
                            &#8203;
                        </span>
                        <Transition.Child
                            as={Fragment}
                            enter="tw-ease-out tw-duration-300"
                            enterFrom="tw-opacity-0 tw-translate-y-4 sm:tw-translate-y-0 sm:tw-scale-95"
                            enterTo="tw-opacity-100 tw-translate-y-0 sm:tw-scale-100"
                            leave="tw-ease-in tw-duration-200"
                            leaveFrom="tw-opacity-100 tw-translate-y-0 sm:tw-scale-100"
                            leaveTo="tw-opacity-0 tw-translate-y-4 sm:tw-translate-y-0 sm:tw-scale-95"
                        >
                            <div className="tw-inline-block tw-align-bottom tw-bg-white tw-rounded-lg tw-text-left tw-overflow-hidden tw-shadow-xl tw-transform tw-transition-all sm:tw-my-8 sm:tw-align-middle sm:tw-max-w-4xl sm:tw-w-full">
                                <div className="tw-bg-white tw-px-4 tw-pt-5 tw-pb-4 sm:tw-p-6 sm:tw-pb-4">
                                    <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                                        <DialogTitle as="h3" className="tw-text-lg tw-leading-6 tw-font-medium tw-text-gray-900">
                                            {title}
                                        </DialogTitle>
                                        <button
                                            type="button"
                                            className="tw-text-gray-400 hover:tw-text-gray-600"
                                            onClick={() => setOpen(false)}
                                        >
                                            <span className="tw-sr-only">{te(translations.edit.distribution.close)}</span>
                                            <svg className="tw-h-6 tw-w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                    
                                    <div className="tw-w-full tw-h-96 tw-mb-4">
                                        {blobUrl && (
                                            <iframe 
                                                src={blobUrl} 
                                                width="100%" 
                                                height="100%" 
                                                className="tw-border tw-border-gray-200 tw-rounded"
                                                title="PDF Preview"
                                            />
                                        )}
                                    </div>
                                </div>
                                
                                <div className="tw-bg-gray-50 tw-px-4 tw-py-3 sm:tw-px-6 tw-flex tw-justify-end tw-space-x-3">
                                    <button
                                        type="button"
                                        className="tw-inline-flex tw-justify-center tw-py-2 tw-px-4 tw-border tw-border-transparent tw-shadow-sm tw-text-sm tw-font-medium tw-rounded-md tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-blue-500"
                                        onClick={handleDownload}
                                    >
                                        {te(translations.edit.distribution.download)}
                                    </button>
                                    <button
                                        type="button"
                                        className="tw-inline-flex tw-justify-center tw-py-2 tw-px-4 tw-border tw-border-transparent tw-shadow-sm tw-text-sm tw-font-medium tw-rounded-md tw-text-white tw-bg-green-600 hover:tw-bg-green-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-green-500"
                                        onClick={handlePrint}
                                    >
                                        {te(translations.edit.distribution.print)}
                                    </button>
                                    <button
                                        type="button"
                                        className="tw-inline-flex tw-justify-center tw-py-2 tw-px-4 tw-border tw-border-gray-300 tw-shadow-sm tw-text-sm tw-font-medium tw-rounded-md tw-text-gray-700 tw-bg-white hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-indigo-500"
                                        onClick={() => setOpen(false)}
                                        ref={cancelButtonRef}
                                    >
                                        {te(translations.edit.distribution.close)}
                                    </button>
                                </div>
                            </div>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}